import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertCircle,
  ArrowUpRight,
  CalendarDays,
  Clock,
  ShieldCheck,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { toast } from "sonner";

import { Container } from "@/platform/layout/container";
import { Button } from "@/platform/ui/button";
import { Badge } from "@/platform/ui/badge";
import { Card } from "@/platform/ui/card";
import { Input } from "@/platform/ui/input";
import { Label } from "@/platform/ui/label";
import { onboardingSchema, type OnboardingValues } from "@/platform/auth/schemas";
import { completeOnboarding } from "@/platform/auth/api";
import { useSession } from "@/platform/auth/use-session";
import { useProfile } from "@/platform/auth/use-profile";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";
import { TrophySpot } from "@/platform/ui/illustrations/spot";

const DEFAULT_EXAM_DATE = "2026-11-22";
const DEFAULT_HOURS = 12;

export function OnboardingPage() {
  const navigate = useNavigate();
  const { session } = useSession();
  const { profile, refetch } = useProfile();
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<OnboardingValues>({
    resolver: zodResolver(onboardingSchema),
    defaultValues: {
      exam_date: DEFAULT_EXAM_DATE,
      weekly_hours: DEFAULT_HOURS,
      consent_research: false,
    },
  });

  // Repopula com o que estava no perfil (não-perdível) ou com o último rascunho
  // local (perdível em qualquer aba/limpeza). Sem isto, refresh durante
  // onboarding zera valores que a aluna acabou de digitar.
  useEffect(() => {
    if (profile?.exam_date) form.setValue("exam_date", profile.exam_date);
    if (profile?.weekly_hours != null) form.setValue("weekly_hours", Number(profile.weekly_hours));
    if (profile?.consent_research) form.setValue("consent_research", profile.consent_research);

    try {
      const draft = localStorage.getItem("onboarding_draft");
      if (draft) {
        const parsed = JSON.parse(draft) as Partial<OnboardingValues>;
        if (parsed.exam_date) form.setValue("exam_date", parsed.exam_date);
        if (typeof parsed.weekly_hours === "number") form.setValue("weekly_hours", parsed.weekly_hours);
        if (typeof parsed.consent_research === "boolean") {
          form.setValue("consent_research", parsed.consent_research);
        }
      }
    } catch {
      // localStorage indisponível ou JSON corrompido — ignora.
    }
  }, [profile, form]);

  // Salva rascunho local a cada mudança para sobreviver a refresh acidental.
  useEffect(() => {
    const sub = form.watch((values) => {
      try {
        localStorage.setItem("onboarding_draft", JSON.stringify(values));
      } catch {
        // Ignora.
      }
    });
    return () => sub.unsubscribe();
  }, [form]);

  async function onSubmit(values: OnboardingValues) {
    if (!session) {
      toast.error("Sessão expirada. Faça login novamente.");
      navigate("/entrar", { replace: true });
      return;
    }
    setSubmitting(true);
    try {
      await completeOnboarding(session.user.id, values);
      try { localStorage.removeItem("onboarding_draft"); } catch {}
      await refetch();
      toast.success("Pronto. Bem-vinda.");
      navigate("/painel", { replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Não foi possível salvar.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
      <Blob
        color="violet"
        className="pointer-events-none absolute -left-32 -top-20 w-96 opacity-30 blur-2xl animate-blob"
      />
      <Blob
        color="mint"
        className="pointer-events-none absolute -right-32 bottom-0 w-96 opacity-25 blur-2xl animate-blob"
      />
      <SoftStar
        color="yellow"
        className="pointer-events-none absolute right-10 top-32 w-14 opacity-60 animate-float"
      />

      <Container size="wide" className="relative py-16 md:py-20">
        <header className="animate-fade-up mx-auto max-w-2xl text-center">
          <Badge variant="accentSolid" className="mx-auto w-fit">
            <Sparkles className="size-3" aria-hidden />
            Onboarding · passo final
          </Badge>
          <h1 className="mt-6 font-display text-[length:var(--text-display-lg)] font-semibold leading-[var(--text-display-lg--line-height)] tracking-[var(--text-display-lg--letter-spacing)] text-balance">
            Três respostas e seu <span className="text-gradient-accent">painel</span> está pronto.
          </h1>
          <p className="mt-5 text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)] leading-[var(--text-body-lg--line-height)] text-pretty">
            Calibramos o plano semanal a partir da data prevista da prova e quantas horas você
            pretende dedicar.
          </p>
        </header>

        <div className="mx-auto mt-12 max-w-2xl">
          <Card className="overflow-hidden p-0 animate-fade-up [animation-delay:120ms]">
            <div className="grid gap-px bg-[color:var(--color-border)] sm:grid-cols-[1fr_auto]">
              <div className="bg-[color:var(--color-paper)] p-7 sm:p-8">
                <Badge variant="accent">Seu contexto</Badge>
                <p className="mt-2 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
                  Pode mudar tudo depois nas configurações.
                </p>
              </div>
              <div className="hidden sm:block bg-[color:var(--color-paper)] p-4">
                <TrophySpot className="size-16 animate-float" />
              </div>
            </div>

            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-7 p-7 sm:p-8" noValidate>
              <FormField
                icon={CalendarDays}
                tone="violet"
                label="Data prevista da prova"
                hint="A prova Einstein historicamente cai em novembro. Pode ajustar."
                error={form.formState.errors.exam_date?.message}
                input={<Input type="date" {...form.register("exam_date")} />}
              />
              <FormField
                icon={Clock}
                tone="mint"
                label="Horas de estudo por semana"
                hint="Honestidade aqui rende mais que ambição. O plano se adapta."
                error={form.formState.errors.weekly_hours?.message}
                input={
                  <Input
                    type="number"
                    min={1}
                    max={60}
                    step={1}
                    {...form.register("weekly_hours", { valueAsNumber: true })}
                  />
                }
              />

              <div className="space-y-3 rounded-lg border border-[--color-border] bg-[color:var(--color-paper)] p-5">
                <CheckboxField
                  label="Quero contribuir com pesquisa anônima"
                  hint="Autorizo uso anônimo das minhas redações e tentativas para melhorar o produto. Opt-in, revogável a qualquer momento."
                  register={form.register("consent_research")}
                />
              </div>
              <p className="text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
                Você já aceitou os{" "}
                <Link to="/termos" className="link font-medium">
                  Termos
                </Link>{" "}
                e a{" "}
                <Link to="/privacidade" className="link font-medium">
                  Política de privacidade
                </Link>{" "}
                ao criar sua conta.
              </p>

              <div className="flex items-center gap-2.5 rounded-lg bg-[color:var(--color-mint-soft)] px-4 py-3 text-[length:var(--text-caption)] text-[color:var(--color-mint-deep)]">
                <ShieldCheck className="size-4 shrink-0" aria-hidden />
                <span>Seus dados ficam só com você. Ninguém na plataforma vê seu progresso individual.</span>
              </div>

              <Button type="submit" size="xl" className="w-full" loading={submitting}>
                Salvar e ver meu painel
                <ArrowUpRight className="size-4" aria-hidden />
              </Button>
            </form>
          </Card>
        </div>
      </Container>
    </div>
  );
}

type Tone = "violet" | "pink" | "mint" | "yellow" | "coral" | "sky";
const TONE_BG: Record<Tone, string> = {
  violet: "bg-[color:var(--color-accent-soft)] text-[color:var(--color-accent-soft-foreground)]",
  pink: "bg-[color:var(--color-pink-soft)] text-[color:var(--color-pink-deep)]",
  mint: "bg-[color:var(--color-mint-soft)] text-[color:var(--color-mint-deep)]",
  yellow: "bg-[color:var(--color-yellow-soft)] text-[color:var(--color-yellow-deep)]",
  coral: "bg-[color:var(--color-coral-soft)] text-[color:var(--color-coral-deep)]",
  sky: "bg-[color:var(--color-sky-soft)] text-[color:var(--color-sky-deep)]",
};

function FormField({
  icon: Icon,
  tone,
  label,
  hint,
  error,
  input,
}: {
  icon: LucideIcon;
  tone: Tone;
  label: string;
  hint?: string;
  error?: string;
  input: React.ReactNode;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-[auto_1fr] sm:items-start sm:gap-5">
      <span className={`chip size-11 shrink-0 ${TONE_BG[tone]}`}>
        <Icon className="size-5" aria-hidden />
      </span>
      <div className="space-y-1.5">
        <Label>{label}</Label>
        {input}
        {error ? (
          <p className="flex items-center gap-1.5 text-[length:var(--text-caption)] text-[color:var(--color-danger)]">
            <AlertCircle className="size-3.5" aria-hidden />
            {error}
          </p>
        ) : hint ? (
          <p className="text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">{hint}</p>
        ) : null}
      </div>
    </div>
  );
}

function CheckboxField({
  label,
  hint,
  error,
  register,
}: {
  label: string;
  hint?: string;
  error?: string;
  register: ReturnType<ReturnType<typeof useForm<OnboardingValues>>["register"]>;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input
        type="checkbox"
        {...register}
        className="mt-0.5 size-5 rounded-xs border-2 border-[--color-border] accent-[--color-accent] cursor-pointer"
      />
      <span className="space-y-1">
        <span className="block text-[length:var(--text-body-sm)] font-medium text-[color:var(--color-foreground)]">{label}</span>
        {error ? (
          <span className="flex items-center gap-1.5 text-[length:var(--text-caption)] text-[color:var(--color-danger)]">
            <AlertCircle className="size-3.5" aria-hidden />
            {error}
          </span>
        ) : hint ? (
          <span className="block text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
            {hint}
          </span>
        ) : null}
      </span>
    </label>
  );
}
